import api from '@/utils/axios';
import type {
  CompanyProfile,
  EntityOrder,
  EntityStats,
  EntityType,
  PaginatedResponse,
} from '@/shared/types/company.types';
import {
  getMockProfile,
  getMockOrders,
  getMockStats,
} from '@/domains/company/mock/company.mock';

const USE_MOCK = true;

function unwrapItem<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export async function fetchCompanyProfile(
  userId: number,
  entityType: EntityType,
): Promise<CompanyProfile> {
  if (USE_MOCK) return getMockProfile(userId, entityType);

  try {
    const res = await api.get(`/users/${userId}`);
    const user = unwrapItem<Record<string, unknown>>(res.data);
    if (user.company_profile) {
      return user.company_profile as CompanyProfile;
    }
  } catch {
    /* fallback mock */
  }
  return getMockProfile(userId, entityType);
}

export async function updateCompanyProfile(
  profileId: number,
  data: Partial<CompanyProfile>,
): Promise<CompanyProfile> {
  if (USE_MOCK) {
    return { ...getMockProfile(1, data.entity_type ?? 'pme'), ...data, id: profileId };
  }
  const res = await api.patch(`/company-profiles/${profileId}`, data);
  return unwrapItem<CompanyProfile>(res.data);
}

export async function fetchEntityOrders(
  page = 1,
  perPage = 10,
  search = '',
): Promise<PaginatedResponse<EntityOrder>> {
  if (USE_MOCK) return getMockOrders(page, perPage, search);

  const res = await api.get('/orders', { params: { page, per_page: perPage, search } });
  return res.data;
}

export async function fetchEntityStats(): Promise<EntityStats> {
  if (USE_MOCK) return getMockStats();

  const res = await api.get('/company/stats');
  return unwrapItem<EntityStats>(res.data);
}
